import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecentLocations } from 'hooks/useRecentLocations';
import { useUserLocationsQuery } from 'hooks/useUserLocationsQuery';
import { updateCurrentClientColor } from 'lib/utils';
import Modal from '../Modal/Modal';
import KSLocationModal from '../KSLocationModal';
import './LocationNotFoundModal.scss';

export const LocationNotFoundModal: React.FC = () => {
  const navigate = useNavigate();
  const { showLocationNotFoundModal, setShowLocationNotFoundModal, recentLocations, setRecentLocations } =
    useRecentLocations();
  const { data: locations } = useUserLocationsQuery();
  const [isLocationModalOpen, setIsLocationModalOpen] = React.useState(false);

  const handleLocationChange = (newLocationId: string) => {
    const currentIndex = recentLocations.findIndex(loc => loc.locationId === newLocationId);
    if (currentIndex !== -1) {
      updateCurrentClientColor(currentIndex);
    }

    localStorage.setItem('hone:locationId', newLocationId);
    setShowLocationNotFoundModal(false);
    navigate(`/app/location/${newLocationId}/new-dashboard`);
  };

  const handleAddToRecentLocations = (newLocationId: string) => {
    const selectedLocation = (locations as HoneLocationUser[])?.find(loc => loc.locationId === newLocationId);
    if (!selectedLocation) return;

    setRecentLocations((prevRecentLocations: HoneLocationUser[]) => {
      const updatedLocations = [
        selectedLocation,
        ...prevRecentLocations.filter(loc => loc.locationId !== newLocationId),
      ];
      return updatedLocations.slice(0, 8);
    });
  };

  const handleRemoveFromRecentLocations = (removeLocationId: string) => {
    setRecentLocations((prevRecentLocations: HoneLocationUser[]) => {
      return prevRecentLocations.filter(loc => loc.locationId !== removeLocationId);
    });
  };

  const handleClose = () => {
    setShowLocationNotFoundModal(false);
  };

  return (
    <Modal isOpen={showLocationNotFoundModal} onClose={handleClose} className="location-not-found-modal">
      <div className="location-not-found-modal__content">
        <button className="location-not-found-modal__close-button" onClick={handleClose} aria-label="Close">
          ×
        </button>
        <div className="location-not-found-modal__header">
          <h2 className="location-not-found-modal__title">Location Not Available</h2>
          <p className="location-not-found-modal__description">
            The location you're trying to access is no longer available or has been removed.
          </p>
          <p className="location-not-found-modal__description">
            We have cleared all non-existing locations from your recent locations to match your current access
            permissions.
          </p>
        </div>

        <div className="location-not-found-modal__actions">
          <button className="location-not-found-modal__button" onClick={() => setIsLocationModalOpen(true)}>
            Select a Location
          </button>
          <KSLocationModal
            handleClose={() => setIsLocationModalOpen(false)}
            recentLocations={recentLocations}
            onLocationChange={handleLocationChange}
            onAddToRecentLocations={handleAddToRecentLocations}
            onRemoveFromRecentLocations={handleRemoveFromRecentLocations}
            collapsePinnedLocations={false}
            isOpen={isLocationModalOpen}
            locations={(locations as HoneLocationUser[]) || []}
            preventClickAway={false}
            autoFocus={true}
            placement="center"
          />
        </div>
      </div>
    </Modal>
  );
};
